import { CreationToolFrame, CreationToolSourceFile } from 'creation/src/creation-tools/types'

/**
 * Releases a canvas' backing store. Dropping the reference is not enough —
 * browsers do not reclaim canvas memory promptly, and a 30s clip at 30fps
 * holds ~900 of these.
 */
const releaseCanvas = (canvas?: HTMLCanvasElement) => {
    if (!canvas) return
    canvas.width = 0
    canvas.height = 0
}

export const disposeFrames = (frames: CreationToolFrame[] = []) => {
    frames.forEach((frame) => releaseCanvas(frame?.canvas))
}

export const disposeSources = (sources: CreationToolSourceFile[] = []) => {
    sources.forEach((source) => {
        if (!source) return
        releaseCanvas(source.preview)
        disposeFrames(source.frames)
    })
}

/**
 * Every canvas reachable from a single source: `preview` plus each frame's
 * `canvas`. This is the single place that knows the canvas-bearing shape of
 * `CreationToolSourceFile` — kept in step with `disposeSources`/`disposeFrames`
 * above (same two fields) so collection and release can't drift apart as
 * fields are added.
 */
const canvasesOf = (source?: CreationToolSourceFile): HTMLCanvasElement[] => {
    if (!source) return []
    const frameCanvases = (source.frames ?? [])
        .map((frame) => frame?.canvas)
        .filter((canvas): canvas is HTMLCanvasElement => Boolean(canvas))
    return source.preview ? [source.preview, ...frameCanvases] : frameCanvases
}

/**
 * Releases every canvas reachable from `outgoing` that is not also reachable
 * from `incoming` — compared at the canvas level, not the source-object
 * level. A whole-object reference-identity check (disposeSources' original
 * use in setFiles/reset) is wrong here: a source can be replaced with a new
 * object that still carries some of the same canvases forward (e.g. a video
 * trim step slicing `frames` on the same id, or simply retaining `preview`
 * unchanged), and disposing the old object wholesale would zero canvases the
 * new object still uses. Comparing canvases directly makes that class of
 * aliasing bug impossible regardless of which source object ends up holding
 * a given canvas.
 */
export const disposeUnreachableCanvases = (
    outgoing: CreationToolSourceFile[] = [],
    incoming: CreationToolSourceFile[] = []
) => {
    const reachable = new Set<HTMLCanvasElement>()
    incoming.forEach((source) => canvasesOf(source).forEach((canvas) => reachable.add(canvas)))
    outgoing.forEach((source) =>
        canvasesOf(source).forEach((canvas) => {
            if (!reachable.has(canvas)) releaseCanvas(canvas)
        })
    )
}

/**
 * Releases every frame in `outgoing` whose canvas is not also reachable from
 * `retained` — the frame-level counterpart of `disposeUnreachableCanvases`,
 * and for the same reason: comparison must happen at the canvas level, never
 * at the array or object level.
 *
 * The concrete case this exists for: `continue-button.tsx` hands the cut-out
 * step's own `editCanvas` array straight to `saveFrames`, which the creation
 * store keeps BY REFERENCE as `stepFrames[step]` (create-store.ts:186) — not a
 * copy. The step then unmounts on navigation and would, disposing its own
 * array, zero the very canvases the store is still holding. Passing the
 * store's `stepFrames` as `retained` makes that impossible regardless of which
 * array a given canvas ends up in.
 */
export const disposeUnreachableFrames = (
    outgoing: CreationToolFrame[] = [],
    retained: (CreationToolFrame[] | undefined)[] = []
) => {
    const reachable = new Set<HTMLCanvasElement>()
    retained.forEach((frames) => (frames ?? []).forEach((frame) => frame?.canvas && reachable.add(frame.canvas)))
    outgoing.forEach((frame) => {
        if (frame?.canvas && !reachable.has(frame.canvas)) releaseCanvas(frame.canvas)
    })
}
